import { useState } from 'react';
import { useAuth } from './authContext';
import { checkPasswordBreached } from './passwordBreach';
import { Button, Notice } from '@/ui/primitives';
import { inputClass } from '@/ui/classes';

/**
 * Email + password sign-in, shared by both targets. `allowSignUp` gates the
 * self-service "create an account" path (business only — admin accounts are
 * founder-provisioned, never self-registered) and, with it, the
 * breached-password check (spec §5.1), which only makes sense at the point
 * a password is first chosen.
 */
export function SignInForm({ allowSignUp, onSignedIn }: { allowSignUp: boolean; onSignedIn: () => void }) {
  const { supabase } = useAuth();
  const [mode, setMode] = useState<'sign-in' | 'sign-up'>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The project's confirmation email carries a one-time CODE, not a link
  // (it is shared with the mobile app's sign-up, which verifies the same
  // code). With no code step here, a portal sign-up could never be confirmed.
  const [confirmEmail, setConfirmEmail] = useState<string | null>(null);
  const [code, setCode] = useState('');

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (!confirmEmail) return;
    setError(null);
    setSubmitting(true);
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: confirmEmail,
        token: code.trim(),
        type: 'signup',
      });
      if (verifyError) throw verifyError;
      if (!data.session) throw new Error('That code did not sign you in. Request a new code and try again.');
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    if (!confirmEmail) return;
    setError(null);
    const { error: resendError } = await supabase.auth.resend({ type: 'signup', email: confirmEmail });
    if (resendError) setError(resendError.message);
    else setNotice(`We sent a new code to ${confirmEmail}.`);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      if (mode === 'sign-up') {
        const breach = await checkPasswordBreached(password);
        if (breach.breached) {
          setError(
            `This password has appeared in ${breach.count.toLocaleString()} known data breaches. Choose a different one.`,
          );
          return;
        }
        // Soft, non-blocking notice (fix round 1, item 7) — see
        // passwordBreach.ts's "fails open" note. No PII in the message.
        // Held rather than set immediately: fix round 2 (minor) — the
        // "check your email" notice below used to unconditionally
        // overwrite this one via `setNotice`, silently dropping the
        // breach-check-failed warning whenever both applied in the same
        // submit (email confirmation required AND the breach API was
        // unreachable). Both notices are shown together when both apply.
        const breachNotice = breach.checkFailed
          ? "Couldn't verify this password against known breaches — continuing anyway."
          : null;

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({ email, password });
        if (signUpError) throw signUpError;
        if (!signUpData.session) {
          // Fix round 1, item 11: a project with email confirmation
          // required returns a user with no session — silently doing
          // nothing left the visitor staring at an unchanged form with no
          // idea whether it worked.
          const checkEmailNotice = `Check your email to confirm your account: enter the code we sent to ${email}.`;
          setNotice(breachNotice ? `${breachNotice} ${checkEmailNotice}` : checkEmailNotice);
          setConfirmEmail(email);
          return;
        }
        if (breachNotice) setNotice(breachNotice);
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) {
          // An account that signed up but never confirmed: offer the code
          // step instead of a dead end (the code is valid for an hour; the
          // resend button covers an expired one).
          if (/email not confirmed/i.test(signInError.message)) {
            setConfirmEmail(email);
            setNotice(`Your email isn't confirmed yet. Enter the code we sent to ${email}, or send a new one.`);
            return;
          }
          throw signInError;
        }
      }
      onSignedIn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (confirmEmail) {
    return (
      <form onSubmit={handleVerifyCode} className="flex flex-col gap-md">
        <label className="flex flex-col gap-xs text-small font-semibold text-ink-2" htmlFor="confirm-code">
          Confirmation code
          <input
            id="confirm-code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6,10}"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className={inputClass}
          />
        </label>
        {notice ? <Notice role="status">{notice}</Notice> : null}
        {error ? <Notice tone="danger" role="alert">{error}</Notice> : null}
        <Button type="submit" size="lg" loading={submitting} disabled={code.length < 6} className="mt-xs w-full">
          {submitting ? 'Please wait…' : 'Confirm and sign in'}
        </Button>
        <button type="button" onClick={handleResendCode} className="self-center text-small font-semibold text-canyon hover:underline">
          Send a new code
        </button>
        <button
          type="button"
          onClick={() => {
            setConfirmEmail(null);
            setCode('');
            setError(null);
            setNotice(null);
          }}
          className="self-center text-small font-semibold text-canyon hover:underline"
        >
          Use a different email
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-md">
      <label className="flex flex-col gap-xs text-small font-semibold text-ink-2" htmlFor="email">
        Email
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
      </label>

      <label className="flex flex-col gap-xs text-small font-semibold text-ink-2" htmlFor="password">
        Password
        <input
          id="password"
          name="password"
          type="password"
          autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
          minLength={8}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
        />
      </label>

      {notice ? <Notice role="status">{notice}</Notice> : null}
      {error ? <Notice tone="danger" role="alert">{error}</Notice> : null}

      <Button type="submit" size="lg" loading={submitting} className="mt-xs w-full">
        {submitting ? 'Please wait…' : mode === 'sign-up' ? 'Create account' : 'Sign in'}
      </Button>

      {allowSignUp ? (
        <button
          type="button"
          onClick={() => {
            setMode((m) => (m === 'sign-up' ? 'sign-in' : 'sign-up'));
            setError(null);
            setNotice(null);
          }}
          className="self-center text-small font-semibold text-canyon hover:underline"
        >
          {mode === 'sign-up' ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
        </button>
      ) : null}
    </form>
  );
}
